import React, { useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { useQuery } from 'react-query';
import type { QueryObserverResult } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useGlobalContext } from '../state/GlobalContext';
import { getCurrencyFormatter } from '../../utils';
import {
  getAllProducts,
  getProductsByHandle,
  ProductsDataResponse,
} from '../api';
import ProductSlidePanel from './ProductSlidePanel';
import SelectProduct from './SelectProduct';
import { formatSelectorProduct } from './selectorUtils';
import BundleCustomize from './BundleCustomize';
import { getGidFromIdForResource } from '../../../utils/gid-utils';

const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');

const LIQUID_BATCH_SIZE = 20;
export interface ProductSwapProps {
  product: Product;
  subscription: Subscription;
  updateProduct: (
    productId: string,
    quantity: number,
    lineId: string,
    updatedProduct: MinProduct,
  ) => void;
  updateForBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: SellingPlan,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
  ) => void;
  sellingPlans: SellingPlan[];
  shopConfiguration: ShopConfiguration;
  closeModal: () => void;
  subscriptionCurrencyCode: string;
}

interface UseStore {
  isFetching: boolean;
  hasErrors: boolean;
  storeProducts: GlobalProduct[];
  loadMoreProducts?: () => void;
  refetch: () => Promise<QueryObserverResult<ProductsDataResponse>>;
}

const useProducts = ({
  closeModal,
  product,
  subscriptionCurrencyCode,
  subscriptionId,
}): UseStore => {
  const { customerId, authToken } = useGlobalContext();
  const [page, setPage] = React.useState(0);
  const [storeProducts, setStoreProducts] = React.useState<APIDataProduct[]>(
    [],
  );
  const toast = useToast();
  const { t } = useTranslation();

  const {
    data,
    isLoading: isFetching,
    isError: hasErrors,
    refetch,
  } = useQuery(
    ['products', page],
    async () => {
      const shopDomain = product.ShopDomain;
      const lineSellingPlanGid = product.SellingPlanId;

      const products = await getAllProducts({
        customerId,
        authToken,
        shopDomain,
        lineSellingPlanGid,
        page: page + 1,
        itemsPerPage: 50,
      });
      if ('currencyCode' in products && 'productVariants' in products) {
        const currencyCode = products.currencyCode;
        if (currencyCode !== subscriptionCurrencyCode) {
          const productsHandles = uniq(
            products.productVariants.map(
              (productVariant) => productVariant.product.handle,
            ),
          );

          let liquidProducts = {
            products: [],
            countryCode: '',
            currencyCode: '',
          };
          const promises = [];
          for (let i = 0; i < productsHandles.length; i += LIQUID_BATCH_SIZE) {
            promises.push(
              getProductsByHandle({
                customerId,
                authToken,
                subscriptionId,
                productsHandles: productsHandles.slice(
                  i,
                  i + LIQUID_BATCH_SIZE,
                ),
              }),
            );
          }
          const liquidProductsResponses = await Promise.all(promises);

          liquidProducts = {
            countryCode: get(liquidProductsResponses, '0.countryCode'),
            currencyCode: get(liquidProductsResponses, '0.currencyCode'),
            products: liquidProductsResponses.reduce(
              (acc, { products }) => [...acc, ...products],
              [],
            ),
          };
          if (liquidProducts.currencyCode !== subscriptionCurrencyCode) {
            closeModal();
            toast({
              description: t('product_swap.empty'),
              status: 'error',
              isClosable: true,
              position: 'top',
              title: t('product_swap.error'),
            });
            return;
          }

          const newProductVariants = products.productVariants
            .map((productVariant) => {
              const liquidProduct = liquidProducts.products.find(
                (liquidProduct) =>
                  getGidFromIdForResource(liquidProduct.id, 'Product') ===
                  productVariant.product.id,
              );
              if (!liquidProduct) {
                return;
              }
              const liquidVariant = liquidProduct.productData.variants.find(
                ({ id }) =>
                  productVariant.id ===
                  getGidFromIdForResource(id, 'ProductVariant'),
              );

              const minVariantPrice =
                get(liquidProduct, 'productData.price_min') / 100;
              const maxVariantPrice =
                get(liquidProduct, 'productData.price_max') / 100;

              const product = {
                ...productVariant.product,
                description: get(liquidProduct, 'productData.description'),
                descriptionHtml: get(
                  liquidProduct,
                  'productData.descriptionHtml',
                  undefined,
                ),
                priceRangeV2: {
                  minVariantPrice: {
                    amount: minVariantPrice.toFixed(2),
                    currencyCode: subscriptionCurrencyCode,
                  },
                  maxVariantPrice: {
                    amount: maxVariantPrice.toFixed(2),
                    currencyCode: subscriptionCurrencyCode,
                  },
                },
              };

              const priceInScCurrency = +liquidVariant?.price / 100;

              return {
                ...productVariant,
                product,
                price: priceInScCurrency.toFixed(2),
                displayName: liquidVariant.name,
              };
            })
            .filter(Boolean);

          return { productVariants: newProductVariants, meta: products.meta };
        }
      }

      return products as ProductsDataResponse;
    },
    { refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (data?.meta?.page) {
      if ('productVariants' in data && data.productVariants.length) {
        setStoreProducts(
          (storeProducts) =>
            [...storeProducts, ...data.productVariants] as APIDataProduct[],
        );
      }
    }
  }, [data]);

  const loadMoreProducts = useCallback(() => {
    if (data?.meta?.hasNextPage) {
      setPage((page) => page + 1);
    }
  }, [data]);

  return {
    isFetching,
    hasErrors,
    storeProducts: storeProducts
      .map((product) => {
        if (product.variants.length === 0) {
          return null;
        }
        const { formattedProduct } = formatSelectorProduct(product);
        return formattedProduct;
      })
      .filter(Boolean),
    loadMoreProducts,
    refetch,
  };
};
const useSelectAndUpdateProduct = ({
  product,
  storeProducts,
  updateProduct,
  updateForBundle,
  closeModal,
}) => {
  const [selectedProduct, setSelectedProduct] =
    React.useState<GlobalProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = React.useState<GlobalVariant>();
  const [selectedBundle, setSelectedBundle] = React.useState<GlobalProduct>();

  const handleBundleSelected = (bundleProduct: GlobalProduct) => {
    setSelectedBundle(bundleProduct);
  };

  const handleChoose = (productId, isBundle) => {
    const selectedProduct: GlobalProduct = storeProducts.find(
      (p) => p.id === productId,
    );
    if (isBundle) handleBundleSelected(selectedProduct);
    else {
      if (selectedProduct) {
        setSelectedProduct(selectedProduct);
        setSelectedVariant(selectedProduct.variants[0]);
      } else throw new Error('Product not found');
    }
  };

  const goBack = () => {
    closeModal();
    setSelectedProduct(null);
  };

  const selectVariant = (selectedVariant) => {
    const newVariant = selectedProduct?.variants.find(
      (newVariant) => newVariant.id == selectedVariant?.id,
    );
    setSelectedVariant(newVariant);
  };

  const closeSlidePanel = () => {
    setSelectedProduct(null);
    setSelectedVariant(undefined);
  };

  const confirmSelectionProduct = ({ id, quantity }) => {
    const productUpdated = {
      productId: selectedProduct?.id,
      id: selectedVariant?.id,
      title: selectedProduct?.title,
      variantTitle: selectedVariant?.name,
      quantity,
      image: selectedVariant?.image || selectedProduct?.image,
      price: selectedVariant?.price,
      currency: selectedProduct?.priceCurrency,
    };

    const lineId = product.SubscriptionContractLineId;
    updateProduct(id, quantity, lineId, productUpdated);
  };

  return {
    selectedProduct,
    handleChoose,
    goBack,
    selectVariant,
    selectedBundle,
    confirmSelectionProduct,
    confirmSelectionBundle: updateForBundle,
    selectedVariant,
    closeSlidePanel,
  };
};

const ProductSwap: React.FC<ProductSwapProps> = ({
  product,
  subscription,
  sellingPlans,
  closeModal,
  updateProduct,
  shopConfiguration,
  updateForBundle,
  subscriptionCurrencyCode,
}) => {
  const { isFetching, storeProducts, loadMoreProducts, hasErrors, refetch } =
    useProducts({
      closeModal,
      product,
      subscriptionCurrencyCode,
      subscriptionId: subscription.SubscriptionId,
    });

  const {
    selectedProduct,
    handleChoose,
    selectVariant,
    confirmSelectionProduct,
    confirmSelectionBundle,
    selectedVariant,
    selectedBundle,
    closeSlidePanel,
  } = useSelectAndUpdateProduct({
    product,
    storeProducts,
    updateProduct,
    updateForBundle,
    closeModal,
  });

  const isSingleProduct = selectedProduct?.variants.length === 1;
  const repeatedName =
    isSingleProduct && selectedVariant?.name === selectedProduct?.title;
  const variantName = !repeatedName ? selectedVariant?.name : '';
  const format = getCurrencyFormatter(
    selectedVariant?.currency || selectedProduct?.priceCurrency || CURRENCY,
  );
  const formattedPrice = format(
    +(
      selectedVariant?.discountedPrice ||
      selectedVariant?.price ||
      selectedProduct?.price ||
      0
    ),
  );
  const formattedCompareAtPrice = format(
    +(selectedProduct?.compareAtPrice || selectedVariant?.price),
  );

  const shouldShowDiscountedPrice =
    selectedVariant?.discountedPrice &&
    selectedVariant?.discountedPrice < selectedVariant?.price;

  return (
    <>
      <SelectProduct
        hasErrors={hasErrors}
        isFetching={isFetching}
        storeProducts={storeProducts}
        handleChoose={handleChoose}
        loadMoreProducts={loadMoreProducts}
        refetch={refetch}
        showDiscountedPrice
      />
      <ProductSlidePanel
        open={!!selectedProduct}
        onClose={closeSlidePanel}
        imgUrl={selectedVariant?.image || selectedProduct?.image}
        price={formattedPrice}
        shouldShowDiscountedPrice={shouldShowDiscountedPrice}
        formattedCompareAtPrice={formattedCompareAtPrice}
        productName={selectedProduct?.title}
        variantName={selectedProduct?.variants.length === 1 ? '' : variantName}
        description={
          selectedProduct?.descriptionHtml || selectedProduct?.description
        }
        selectedProduct={selectedProduct}
        selectedVariant={selectedVariant}
        onSelect={confirmSelectionProduct}
        onConfirm={selectVariant}
        isLoading={!selectedProduct || isEmpty(selectedProduct)}
      />
      {selectedBundle && (
        <BundleCustomize
          subscription={subscription}
          product={product}
          shopConfiguration={shopConfiguration}
          bundleProduct={selectedBundle}
          sellingPlans={sellingPlans}
          onConfirm={confirmSelectionBundle}
          onClose={closeSlidePanel}
        />
      )}
    </>
  );
};

export default ProductSwap;
